import { Tube } from "./Tube";
import type { TrialConfig, TubeConfig, HistoryEvent } from "./types";
import { Move } from "./Move";

export class GameState {
  readonly config: TrialConfig;
  public tubes: Tube[];
  public history: Move[] = [];
  public events: HistoryEvent[] = [];
  private undoStack: Move[] = [];

  private constructor(config: TrialConfig, tubes: Tube[]) {
    this.config = config;
    this.tubes = tubes;
  }

  static fromConfig(cfg: TrialConfig): GameState {
    const tubes = cfg.tubes.map(tc => new Tube(tc.segments, tc.segments.length));
    return new GameState(cfg, tubes);
  }

  pour(i: number, j: number): void {
    const movedColor = this.tubes[i].topColor()!;
    const moved = this.tubes[i].pourInto(this.tubes[j]);
    if (moved > 0) {
      this.history.push({ from: i, to: j, count: moved });
      this.events.push({
        type: "pour",
        from: i,
        to: j,
        count: moved,
        color: movedColor,
        timestamp: Date.now(),
        description: `Poured ${moved} ${movedColor}${moved > 1 ? "s" : ""} from tube ${i} to tube ${j}`,
      });
      this.undoStack = [];
    }
  }

  undo(): void {
    if (!this.config.undoEnabled) return;
    const mv = this.history.pop();
    if (!mv) return;

    const movedColor = this.tubes[mv.to].topColor()!;
    for (let k = 0; k < mv.count; k++) {
      const seg = this.tubes[mv.to].popSegment();
      if (!seg) break;
      this.tubes[mv.from].pushSegment(seg);
    }
    this.tubes[mv.from].revealTop();
    this.tubes[mv.to].revealTop();

    this.undoStack.push(mv);
    this.events.push({
      type: "undo",
      from: mv.to,
      to: mv.from,
      count: mv.count,
      color: movedColor,
      timestamp: Date.now(),
      description: `Undid pour of ${mv.count} ${movedColor}${mv.count > 1 ? "s" : ""} from tube ${mv.from} to tube ${mv.to}`,
    });
  }

  reset(): void {
    this.tubes = this.config.tubes.map(tc => new Tube(tc.segments, tc.segments.length));
    this.history = [];
    this.undoStack = [];
    this.events.push({
      type: "reset",
      from: -1,
      to: -1,
      count: 0,
      color: "",
      timestamp: Date.now(),
      description: "Reset level",
    });
  }

  isSolved(): boolean {
    return this.tubes.every(t => t.isCompleteUniform());
  }

  clone(): GameState {
    const tubes2 = this.tubes.map(t => t.clone());
    const gs = new GameState(this.config, tubes2);
    gs.history = [...this.history];
    gs.undoStack = [...this.undoStack];
    gs.events = [...this.events];
    return gs;
  }

  toJSON() {
    return {
      tubes: this.tubes.map(t => t.segments),
      history: this.history,
      events: this.events,
    };
  }
}
